import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, ThumbsUp, ThumbsDown, Send, Loader2, Check } from "lucide-react";

import { toast } from "sonner";

export function PostSessionFeedback({ sessionId }) {
  const [stars, setStars] = useState(0);
  const [hoverStars, setHoverStars] = useState(0);
  const [kiHelpful, setKiHelpful] = useState(null);
  const [comment, setComment] = useState("");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [alreadyGiven, setAlreadyGiven] = useState(false);

  useEffect(() => {
    const check = async () => {
      try {
        const res = await api.get(`/session-feedback/${sessionId}`);
        if (res.data.feedback) setAlreadyGiven(true);
      } catch { /* silent — feedback check is optional */ }
    };
    if (sessionId) check();
  }, [sessionId]);

  const submit = async () => {
    if (stars === 0 || kiHelpful === null) return;
    setSending(true);
    try {
      await api.post("/session-feedback", {
        session_id: sessionId,
        stars,
        ki_helpful: kiHelpful,
        comment: comment.trim() || null,
      });
      setSent(true);
    } catch { toast.error("Feedback konnte nicht gesendet werden"); }
    setSending(false);
  };

  if (alreadyGiven || sent) {
    return (
      <div className="flex items-center gap-2 p-3 bg-success/5 rounded-lg" data-testid="session-feedback-thanks">
        <Check className="w-4 h-4 text-success" />
        <span className="font-body text-sm text-success">Danke fuer Ihr Feedback!</span>
      </div>
    );
  }

  return (
    <div className="p-5 bg-muted/30 rounded-xl border border-border/50 space-y-4" data-testid="post-session-feedback">
      <p className="font-heading text-sm font-bold">Wie war diese Session?</p>

      {/* Stars */}
      <div>
        <p className="font-body text-xs text-muted-foreground mb-1.5">Ihre Gesamtbewertung</p>
        <div className="flex gap-1" data-testid="session-feedback-stars">
          {[1, 2, 3, 4, 5].map(n => (
            <button
              key={n}
              onMouseEnter={() => setHoverStars(n)}
              onMouseLeave={() => setHoverStars(0)}
              onClick={() => setStars(n === stars ? 0 : n)}
              className="p-0.5 transition-transform hover:scale-110"
              data-testid={`session-star-${n}`}
            >
              <Star
                className={`w-6 h-6 transition-colors ${
                  n <= (hoverStars || stars) ? "text-warning fill-warning" : "text-muted-foreground/30"
                }`}
              />
            </button>
          ))}
        </div>
      </div>

      {/* KI helpful */}
      <div>
        <p className="font-body text-xs text-muted-foreground mb-1.5">Hat Ihnen das KI-Feedback geholfen?</p>
        <div className="flex gap-2" data-testid="session-feedback-helpful">
          <Button
            variant={kiHelpful === true ? "default" : "outline"}
            size="sm"
            className={`font-body gap-1 ${kiHelpful === true ? "bg-success text-white hover:bg-success/90" : ""}`}
            onClick={() => setKiHelpful(true)}
            data-testid="ki-helpful-yes"
          >
            <ThumbsUp className="w-3.5 h-3.5" /> Ja
          </Button>
          <Button
            variant={kiHelpful === false ? "default" : "outline"}
            size="sm"
            className={`font-body gap-1 ${kiHelpful === false ? "bg-error text-white hover:bg-error/90" : ""}`}
            onClick={() => setKiHelpful(false)}
            data-testid="ki-helpful-no"
          >
            <ThumbsDown className="w-3.5 h-3.5" /> Nein
          </Button>
        </div>
      </div>

      {/* Comment */}
      {stars > 0 && kiHelpful !== null && (
        <div>
          <Textarea
            value={comment}
            onChange={e => setComment(e.target.value.slice(0, 500))}
            placeholder="Moechten Sie uns noch etwas mitteilen? (optional)"
            className="font-body text-sm min-h-[60px] resize-none"
            data-testid="session-feedback-comment"
          />
          {comment.length > 0 && (
            <p className="font-body text-[10px] text-muted-foreground text-right mt-0.5">{comment.length}/500</p>
          )}
        </div>
      )}

      {/* Submit */}
      {stars > 0 && kiHelpful !== null && (
        <Button
          onClick={submit}
          disabled={sending}
          size="sm"
          className="font-body bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5"
          data-testid="session-feedback-submit"
        >
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Feedback senden
        </Button>
      )}
    </div>
  );
}
